import { useState, useEffect } from "react";
import API from "@/lib/api";
import { AlertTriangle, AlertCircle, Info, ShieldCheck, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import HelpLink from "@/components/HelpLink";

/**
 * GLP-1 Drug-Interaction warning banner shown on the prescriber's order
 * approval modal. Fetches `GET /api/orders/:id/interactions` and renders
 * the severity-graded warning list.
 *
 * Color logic:
 *   - high     -> red    (mandatory acknowledgment)
 *   - moderate -> amber  (advisable adjustment)
 *   - low      -> slate  (FYI / counseling)
 *   - none     -> emerald clean-screen confirmation
 */
const SEVERITY_META = {
  high:     { label: "HIGH",     bg: "bg-red-50 dark:bg-red-950/40",     border: "border-red-300 dark:border-red-900", text: "text-red-900 dark:text-red-200",     accent: "bg-red-600",      Icon: AlertTriangle },
  moderate: { label: "MODERATE", bg: "bg-amber-50 dark:bg-amber-950/40", border: "border-amber-300 dark:border-amber-900", text: "text-amber-900 dark:text-amber-200", accent: "bg-amber-500",  Icon: AlertCircle },
  low:      { label: "LOW",      bg: "bg-slate-50 dark:bg-slate-900",    border: "border-slate-300 dark:border-slate-700", text: "text-slate-700 dark:text-slate-200", accent: "bg-slate-500",  Icon: Info },
};

const SUMMARY_META = {
  none:     { Icon: ShieldCheck,   bg: "bg-emerald-50 dark:bg-emerald-950/40", border: "border-emerald-200 dark:border-emerald-900", text: "text-emerald-900 dark:text-emerald-200", title: "No GLP-1 interactions detected", subtitle: "The patient's chart contains no known GLP-1 drug interactions or contraindications. Always apply clinical judgment." },
  low:      { Icon: Info,          bg: "bg-slate-50 dark:bg-slate-900",        border: "border-slate-200 dark:border-slate-700",     text: "text-slate-800 dark:text-slate-100",     title: "Counseling-level notes",        subtitle: "Low-severity items below worth mentioning to the patient." },
  moderate: { Icon: AlertCircle,   bg: "bg-amber-50 dark:bg-amber-950/40",     border: "border-amber-200 dark:border-amber-900",     text: "text-amber-900 dark:text-amber-200",     title: "Moderate interactions",         subtitle: "Review carefully. Consider dose adjustment, monitoring, or patient counseling before approval." },
  high:     { Icon: AlertTriangle, bg: "bg-red-50 dark:bg-red-950/40",         border: "border-red-300 dark:border-red-900",         text: "text-red-900 dark:text-red-200",         title: "HIGH-severity warnings",        subtitle: "One or more flags require explicit clinical justification. Do NOT sign without reviewing each item below." },
};

const FIELD_LABEL = {
  current_medications: "Current Medications",
  medical_history: "Medical History",
  allergies: "Allergies",
  any: "Patient profile",
};

export default function DDIWarningBanner({ orderId, onAcknowledgmentsChange, requireAck = false }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [acks, setAcks] = useState({}); // { warning_key: true }

  useEffect(() => {
    if (!orderId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    setAcks({});
    API.get(`/orders/${orderId}/interactions`)
      .then(({ data }) => { if (alive) setReport(data); })
      .catch((e) => { if (alive) setError(e.response?.data?.detail || "Failed to screen interactions"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [orderId]);

  // Notify parent of HIGH-ack state so it can gate the Approve button.
  useEffect(() => {
    if (!onAcknowledgmentsChange) return;
    const highWarnings = (report?.warnings || []).filter((w) => w.severity === "high");
    const allAcked = highWarnings.length === 0 || highWarnings.every((w) => acks[w.key]);
    onAcknowledgmentsChange({
      all_high_acknowledged: allAcked,
      acked_keys: Object.keys(acks).filter((k) => acks[k]),
      high_keys: highWarnings.map((w) => w.key),
    });
  }, [acks, report, onAcknowledgmentsChange]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-sm text-slate-500 dark:text-slate-400" data-testid="ddi-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Screening for GLP-1 interactions...
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-start gap-2 px-4 py-3 rounded-xl border border-red-200 bg-red-50 text-sm text-red-800" data-testid="ddi-error">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <span><b>Interaction screen failed.</b> {error}</span>
      </div>
    );
  }
  if (!report) return null;

  const severity = report.summary_severity || "none";
  const meta = SUMMARY_META[severity];
  const { Icon } = meta;
  const counts = report.counts || { high: 0, moderate: 0, low: 0 };
  const totalWarnings = (report.warnings || []).length;

  return (
    <div className={`rounded-xl border-2 ${meta.border} ${meta.bg} ${meta.text} overflow-hidden`} data-testid={`ddi-banner-${severity}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-black/[0.03] dark:hover:bg-white/[0.03] transition-colors"
        data-testid="ddi-banner-toggle"
      >
        <Icon className="w-5 h-5 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm sm:text-base inline-flex items-center gap-1.5">
            {meta.title}
            <HelpLink sectionId="rx-ddi-engine" title="Training: GLP-1 DDI engine & HIGH-severity acknowledgment" />
          </p>
          <p className="text-xs sm:text-sm opacity-80 mt-0.5">{meta.subtitle}</p>
          {totalWarnings > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="ddi-pills">
              {counts.high > 0    && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-600 text-white">{counts.high} high</span>}
              {counts.moderate > 0 && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500 text-white">{counts.moderate} moderate</span>}
              {counts.low > 0      && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-500 text-white">{counts.low} low</span>}
            </div>
          )}
        </div>
        {totalWarnings > 0 && (expanded ? <ChevronUp className="w-4 h-4 mt-1" /> : <ChevronDown className="w-4 h-4 mt-1" />)}
      </button>

      {expanded && totalWarnings > 0 && (
        <div className="px-3 pb-3 space-y-2" data-testid="ddi-warning-list">
          {(report.warnings || []).map((w) => {
            const m = SEVERITY_META[w.severity] || SEVERITY_META.low;
            const { Icon: WIcon } = m;
            const isHigh = w.severity === "high";
            return (
              <div key={w.key} className={`rounded-lg ${m.bg} ${m.border} border ${m.text} px-3 py-2.5 flex gap-2.5`} data-testid={`ddi-warning-${w.key}`}>
                <span className={`shrink-0 mt-0.5 w-6 h-6 rounded-full ${m.accent} text-white flex items-center justify-center`}>
                  <WIcon className="w-3.5 h-3.5" />
                </span>
                <div className="text-xs sm:text-sm flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-bold uppercase tracking-wider text-[10px]">{m.label}</span>
                    <span className="font-semibold">{w.drug_or_condition}</span>
                    <span className="opacity-60 text-[11px]">- found in {FIELD_LABEL[w.matched_field] || w.matched_field}</span>
                  </div>
                  <p className="opacity-70 italic mt-0.5 text-[11px]" data-testid={`ddi-snippet-${w.key}`}>
                    &quot;...{w.matched_text}...&quot;
                  </p>
                  <p className="mt-1 leading-relaxed">{w.message}</p>
                  {requireAck && isHigh && (
                    <label className="mt-2 flex items-start gap-2 cursor-pointer select-none bg-white/60 dark:bg-black/30 rounded-md px-2 py-1.5 border border-current/30">
                      <input
                        type="checkbox"
                        checked={!!acks[w.key]}
                        onChange={(e) => setAcks((prev) => ({ ...prev, [w.key]: e.target.checked }))}
                        className="mt-0.5 w-4 h-4 accent-red-700"
                        data-testid={`ddi-ack-${w.key}`}
                      />
                      <span className="text-[11px] font-medium">
                        I have reviewed this interaction and counseled / planned monitoring as appropriate.
                      </span>
                    </label>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {expanded && (
        <p className="px-4 pb-3 text-[10px] opacity-60 italic">
          {report.disclaimer || "Rule-based safety net - not a substitute for clinical judgment."}
        </p>
      )}
    </div>
  );
}
